import React, { useState, useEffect, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Typography from "aldoo-ra/Typography"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { showNotification } from "aldoo-ra/Notification"
import { SubmitSupportTicket, GetAgents, GetTags } from "./api"
import {
  PaperClipIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TagIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import Value from "aldoo-ra/Value"
import TagDialog from "./TagDialog"

export default function AddTicketDialog({ isOpen, onClose, onTicketCreated }) {
  const [userProfile] = Value("user-profile")
  const [title, setTitle] = useState("")
  const [author, setAuthor] = useState("")
  const [body, setBody] = useState("")
  const [selectedTags, setSelectedTags] = useState([])
  const [selectedAssignee, setSelectedAssignee] = useState([])
  const [files, setFiles] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [agents, setAgents] = useState([])
  const [availableTags, setAvailableTags] = useState([])
  const [showLeftGradient, setShowLeftGradient] = useState(false)
  const [showRightGradient, setShowRightGradient] = useState(false)
  const [tagDialog, setTagDialog] = useState({
    isOpen: false,
    currentTags: [],
  })

  // Define these constants at the component level for use in multiple functions
  const MAX_FILES = 5
  const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB

  const fileInputRef = useRef(null)
  const scrollContainerRef = useRef(null)

  // Prefill author field if user is logged in
  useEffect(() => {
    if (userProfile?.email) {
      setAuthor(userProfile.email)
    }
  }, [userProfile])

  // Load agents for assignment dropdown
  useEffect(() => {
    async function loadAgents() {
      try {
        const response = await GetAgents()
        if (response.result) {
          // Format agent options properly to ensure ID is used
          const agentOptions = response.data.map((agent) => ({
            value: agent._id || agent.id, // Make sure we're using the agent's ID
            label: agent.name || agent.email,
            email: agent.email,
          }))
          setAgents(agentOptions)
        }
      } catch (error) {
        showNotification({
          message: "Failed to load agents",
          className: "bg-admin_error text-white",
        })
      }
    }
    if (isOpen) {
      loadAgents()
    }
  }, [isOpen])

  // Load available tags
  useEffect(() => {
    async function loadTags() {
      try {
        const response = await GetTags()
        if (response.result) {
          const tagOptions = response.data.map((tag) => ({
            value: tag,
            label: tag,
          }))
          setAvailableTags(tagOptions)
        }
      } catch (error) {
        showNotification({
          message: "Failed to load tags",
          className: "bg-admin_error text-white",
        })
      }
    }
    if (isOpen) {
      loadTags()
    }
  }, [isOpen])

  // Handle file change - FIXED to append new files instead of replacing
  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files)

    // Check if adding these files would exceed the limit
    if (files.length + newFiles.length > MAX_FILES) {
      showNotification({
        message: `You can upload a maximum of ${MAX_FILES} files. You're trying to add ${newFiles.length} more when you already have ${files.length}.`,
        className: "bg-admin_error text-white",
      })
      // Reset input to allow selecting files again
      e.target.value = ""
      return
    }

    // Check for duplicate file names
    const existingFileNames = files.map((file) => file.name)
    const duplicates = newFiles.filter((file) =>
      existingFileNames.includes(file.name)
    )

    if (duplicates.length > 0) {
      showNotification({
        message: `Some files already exist: ${duplicates
          .map((f) => f.name)
          .join(", ")}`,
        className: "bg-admin_warning text-admin_text_inv",
      })
    }

    // Check file sizes
    const oversizedFiles = newFiles.filter((file) => file.size > MAX_FILE_SIZE)
    if (oversizedFiles.length > 0) {
      showNotification({
        message: `Some files exceed the maximum size of 20MB: ${oversizedFiles
          .map((f) => f.name)
          .join(", ")}`,
        className: "bg-admin_error text-white",
      })
      // Don't return here so we can still add the valid files
    }

    // Add only valid files
    const validNewFiles = newFiles.filter(
      (file) =>
        file.size <= MAX_FILE_SIZE && !existingFileNames.includes(file.name)
    )

    // Combine existing files with new files
    setFiles((prevFiles) => [...prevFiles, ...validNewFiles])

    // Reset input to allow selecting the same file again
    e.target.value = ""

    // Update gradients after adding files
    setTimeout(updateGradients, 100)
  }

  // Remove a file at specific index
  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  // Check for overflow and update gradient visibility
  const updateGradients = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current
      setShowLeftGradient(scrollLeft > 0)
      setShowRightGradient(scrollLeft < scrollWidth - clientWidth - 1)
    }
  }

  // Function to scroll left
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" })
    }
  }

  // Function to scroll right
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" })
    }
  }

  // Handle scroll event
  const handleScroll = () => {
    updateGradients()
  }

  // Handle tags from TagDialog
  const handleTagsApplied = (newTags) => {
    // Create tag objects for DropDownSelect
    const tagObjects = newTags.map((tag) => ({
      value: tag,
      label: tag,
    }))

    // Add any new tags to available tags
    newTags.forEach((tag) => {
      if (!availableTags.some((availableTag) => availableTag.value === tag)) {
        setAvailableTags((prev) => [...prev, { value: tag, label: tag }])
      }
    })

    // Update selectedTags state
    setSelectedTags(tagObjects)

    // Close the tag dialog
    setTagDialog({
      isOpen: false,
      currentTags: [],
    })
  }

  // Update gradients on window resize and when files change
  useEffect(() => {
    updateGradients()
    window.addEventListener("resize", updateGradients)
    return () => window.removeEventListener("resize", updateGradients)
  }, [files])

  const resetForm = () => {
    setTitle("")
    setBody("")
    // Keep the author if it's the user's email
    if (!userProfile?.email) {
      setAuthor("")
    }
    setSelectedTags([])
    setSelectedAssignee([])
    setFiles([])
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async () => {
    // Validate required fields
    if (!title.trim()) {
      showNotification({
        message: "Please enter a ticket title",
        className: "bg-admin_error text-white",
      })
      return
    }

    if (!author.trim()) {
      showNotification({
        message: "Please enter an author email",
        className: "bg-admin_error text-white",
      })
      return
    }

    if (!body.trim()) {
      showNotification({
        message: "Please enter a ticket description",
        className: "bg-admin_error text-white",
      })
      return
    }

    // Make sure the author is a valid email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(author)) {
      showNotification({
        message: "Please enter a valid email address",
        className: "bg-admin_error text-white",
      })
      return
    }

    if (selectedTags.length === 0) {
      showNotification({
        message: "Please select at least one tag",
        className: "bg-admin_error text-white",
      })
      return
    }

    // Validate file size and count
    // Using the component-level constants now
    if (files.length > MAX_FILES) {
      showNotification({
        message: `You can upload a maximum of ${MAX_FILES} files`,
        className: "bg-admin_error text-white",
      })
      return
    }

    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE)
    if (oversizedFiles.length > 0) {
      showNotification({
        message: `Some files exceed the maximum size of 20MB: ${oversizedFiles
          .map((f) => f.name)
          .join(", ")}`,
        className: "bg-admin_error text-white",
      })
      return
    }

    try {
      setSubmitting(true)

      // Extract all tag values and ensure no duplicates
      const allTags = [...new Set(selectedTags.map((tag) => tag.value))]

      // Check if we have tags - this is critical to prevent the no_tags error
      if (allTags.length === 0) {
        showNotification({
          message: "Please select at least one tag",
          className: "bg-admin_error text-white",
        })
        return
      }

      // Prepare payload for API
      const payload = {
        summary: title,
        description: body,
        email: author,
        tags: allTags, // Make sure tags is an array with at least one element
      }

      // Add assignee if selected
      if (selectedAssignee.length > 0) {
        const assigneeId = selectedAssignee[0].value
        if (assigneeId) {
          payload.assignedTo = assigneeId
        }
      }

      // Add files if any
      if (files.length > 0) {
        payload.files = files
      }

      const response = await SubmitSupportTicket(payload)

      if (response && response.result) {
        showNotification({
          message: "Ticket created successfully",
          className: "bg-admin_primary text-admin_text_inv",
        })
        resetForm()
        onTicketCreated &&
          onTicketCreated(response.ticket?.ticketID || response.data?.ticketID)
        onClose()
      } else {
        // Show specific error message if available
        const errorMessage =
          response?.message || `Error: ${response?.error || "Unknown error"}`

        console.error("API returned error:", response)
        showNotification({
          message: errorMessage,
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      console.error("Ticket creation error:", error)
      showNotification({
        message:
          error.message ||
          "Failed to create ticket - check console for details",
        className: "bg-admin_error text-white",
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Ticket"
      variant="admin_centered"
      className="p-4"
    >
      <div className="space-y-4">
        {/* Tags and Assignee */}
        <div className="grid grid-cols-2 gap-4">
          {/* Tags */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <Typography variant="admin_body_accent" className="my-0">
                Tags
                <span className="text-admin_error">*</span>
              </Typography>

              <Button
                variant="text"
                className="flex items-center text-admin_primary hover:text-admin_primary/80 text-sm"
                onClick={() =>
                  setTagDialog({
                    isOpen: true,
                    currentTags: selectedTags.map((tag) => tag.value),
                  })
                }
              >
                <TagIcon className="w-4 h-4 mr-1" />
                Manage Tags
              </Button>
            </div>

            <DropDownSelect
              variant="admin"
              options={availableTags}
              value={selectedTags}
              onChange={(values) => setSelectedTags(values || [])}
              className="w-full"
              searchEnabled
              multiSelection
              placeholder="Select tags"
            />
            {selectedTags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {selectedTags.map((tag, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1 px-2 py-1 bg-admin_primary/10 border border-admin_primary/20 rounded-full text-xs"
                  >
                    <span className="text-admin_text">{tag.label}</span>

                    <button
                      onClick={() => {
                        setSelectedTags((prev) =>
                          prev.filter((_, i) => i !== index)
                        )
                      }}
                      className="text-admin_primary hover:bg-admin_neutral_1/50 rounded-full p-0.5 ml-1"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Assign */}
          <div>
            <Typography variant="admin_body_accent" className="mt-0 mb-1">
              Assign
            </Typography>

            <DropDownSelect
              variant="admin"
              options={agents}
              value={selectedAssignee}
              onChange={(values) => {
                if (values && values.length > 0) {
                  // We only need one selected assignee
                  setSelectedAssignee([values[0]])
                } else {
                  setSelectedAssignee([])
                }
              }}
              className="w-full"
              searchEnabled
              placeholder="Assign to agent"
              valueField="value" // Ensure we're using the ID as the value
              labelField="label" // Use the agent's name as the label
            />
          </div>
        </div>

        {/* Ticket Title */}
        <Input
          variant="admin"
          type="text"
          label="Ticket Title"
          placeholder="Enter ticket title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="bg-transparent text-admin_text placeholder-admin_neutral_3"
          inputClassName="bg-transparent text-admin_text"
          styleOverrides={{
            label: "block text-base font-bold text-admin_text mb-1",
          }}
        />

        {/* Author */}
        <Input
          variant="admin"
          type="email"
          label="Author Email"
          placeholder="Email address"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          className="bg-transparent text-admin_text placeholder-admin_neutral_3"
          inputClassName="bg-transparent text-admin_text"
          styleOverrides={{
            label: "block text-base font-bold text-admin_text mb-1",
          }}
        />

        {/* Body */}
        <Input
          variant="admin"
          label="Body"
          multiline={true}
          placeholder="Enter ticket description"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="bg-transparent text-admin_text placeholder-admin_neutral_3 min-h-48"
          inputClassName="bg-transparent text-admin_text"
          styleOverrides={{
            label: "block text-base font-bold text-admin_text mb-1",
          }}
        />

        {/* Attachments */}
        <div>
          <Typography variant="admin_body_accent" className="mt-0 mb-1">
            Attachments
          </Typography>

          <Button
            variant="admin_secondary"
            className="flex items-center gap-2"
            onClick={() => fileInputRef.current.click()}
          >
            <PaperClipIcon className="w-5 h-5" />
            Attach Files
          </Button>

          <Input
            variant="admin"
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />

          {files.length > 0 && (
            <div className="mt-1 relative">
              {/* Left gradient overlay */}
              {showLeftGradient && (
                <div
                  className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-admin_paper via-admin_paper/80 to-transparent z-10 flex items-center cursor-pointer"
                  onClick={scrollLeft}
                >
                  <ChevronLeftIcon className="w-5 h-5 text-admin_text_secondary ml-1" />
                </div>
              )}

              {/* Scrollable container */}
              <div
                ref={scrollContainerRef}
                className="overflow-hidden"
                onScroll={handleScroll}
                style={{
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                <div className="flex gap-2 py-1">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 px-3 py-1.5 bg-admin_paper/5 border border-admin_bordered rounded-full text-sm whitespace-nowrap flex-shrink-0 "
                    >
                      <PaperClipIcon className="w-4 h-4 text-admin_text_secondary" />

                      <Typography
                        variant="admin_txt1"
                        className="text-admin_text my-0"
                      >
                        {file.name}
                      </Typography>

                      <button
                        onClick={() => removeFile(index)}
                        className="text-admin_text hover:bg-admin_neutral_1/50 rounded-full p-0.5 ml-1"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right gradient overlay */}
              {showRightGradient && (
                <div
                  className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-admin_paper via-admin_paper/80 to-transparent z-10 flex items-center justify-end cursor-pointer"
                  onClick={scrollRight}
                >
                  <ChevronRightIcon className="w-5 h-5 text-admin_text_secondary mr-1" />
                </div>
              )}
            </div>
          )}

          {/* Add CSS for hiding scrollbar */}
          <style jsx="true">{`
            div::-webkit-scrollbar {
              display: none;
            }
          `}</style>
        </div>

        {/* Action Button Container */}
        <div className="flex justify-end">
          <Button
            variant="admin_primary"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6"
          >
            {submitting ? "Creating..." : "Create Ticket"}
          </Button>
        </div>
      </div>

      {/* TagDialog for managing tags */}
      {tagDialog.isOpen && (
        <TagDialog
          isOpen={tagDialog.isOpen}
          ticketId={null} // No ticketId for new tickets
          ticketLabel={title || "New Ticket"}
          currentTags={tagDialog.currentTags}
          onClose={() =>
            setTagDialog({
              isOpen: false,
              currentTags: [],
            })
          }
          onTagApplied={handleTagsApplied} // Use new prop instead of onUpdated
          ticket={{ ticketID: null }} // Pass empty ticket object to indicate it's a new ticket
        />
      )}
    </Modal>
  )
}
